import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Search, TrendingDown, Camera, Upload, X, Image as ImageIcon, Pencil } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subDays, eachDayOfInterval, isSameDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePharmacies } from '../context/PharmacyContext';
import SimpleDateRangePicker from '../components/SimpleDateRangePicker';
import ModernDateRangePicker from '../components/ModernDateRangePicker';
import SuperAdminPharmacyFilter from '../components/SuperAdminPharmacyFilter';
import ExpensePharmacyFilter from '../components/ExpensePharmacyFilter';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import useGranularPermissions from '../hooks/useGranularPermissions';
import { LineChart, Line, PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ExpenseWizard from '../components/ExpenseWizard';
import { FilterableHeader, useTableFilters } from '../components/TableFilters';
import { EXPENSE_CATEGORIES, getExpenseVisuals } from '../utils/expenseCategories';
import { toSafeISO } from '../utils/dateNormalize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function Expenses() {
  const { getPharmacyIdsParam, selectedPharmacyIds } = usePharmacies();
  const {
    canCreate,
    canUpdate,
    canDelete,
    canViewHistory,
    canViewTotals,
    canFilterPeriod,
    canViewAllUsers
  } = useGranularPermissions();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [currentUser] = useState(() => JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user')));
  const [viewImageDialog, setViewImageDialog] = useState(false);
  const [viewingImage, setViewingImage] = useState(null);
  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState([]);
  const [repartitionPeriod, setRepartitionPeriod] = useState('Mois');
  const [evolutionPeriod, setEvolutionPeriod] = useState('Mois');
  const [activeTab, setActiveTab] = useState('Toutes');

  // Fonction pour obtenir une couleur unique par pharmacie
  const getPharmacyColor = (pharmacyName) => {
    const colors = [
      { bg: 'bg-emerald-100', text: 'text-emerald-700' },      // Vert
      { bg: 'bg-blue-100', text: 'text-blue-700' },            // Bleu
      { bg: 'bg-purple-100', text: 'text-purple-700' },        // Violet
      { bg: 'bg-pink-100', text: 'text-pink-700' },            // Rose
      { bg: 'bg-amber-100', text: 'text-amber-700' },          // Ambre
      { bg: 'bg-cyan-100', text: 'text-cyan-700' },            // Cyan
      { bg: 'bg-indigo-100', text: 'text-indigo-700' },        // Indigo
      { bg: 'bg-teal-100', text: 'text-teal-700' },            // Teal
      { bg: 'bg-lime-100', text: 'text-lime-700' },            // Lime
      { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },      // Fuchsia
    ];
    
    if (!pharmacyName) return colors[0];
    
    // Hash simple basé sur le nom de la pharmacie
    let hash = 0;
    for (let i = 0; i < pharmacyName.length; i++) {
      hash = pharmacyName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),  // 1er du mois courant
    to: endOfDay(new Date())  // Aujourd'hui
  });

  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    amount: '',
    category_id: '',
    parent_category: '', // Main category (Achat, Charge, Salaire)
    description: '',
    payment_method: 'cash',
    supplier: '',
    invoice_number: '',
    invoice_image_url: '',
  });

  useEffect(() => {
    fetchData();
  }, [selectedPharmacyIds, dateRange, superAdminPharmacyFilter]);
  
  // Separate useEffect for analysis to avoid reference error
  useEffect(() => {
    const loadAnalysis = async () => {
      try {
        let pharmacyIds;
        if (currentUser?.role === 'super_admin') {
          pharmacyIds = superAdminPharmacyFilter === 'all' ? 'all' : superAdminPharmacyFilter;
        } else {
          pharmacyIds = getPharmacyIdsParam();
        }
        
        const start = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd'T'00:00:00") : "2000-01-01T00:00:00";
        const end = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd'T'23:59:59") : "2100-12-31T23:59:59";
        
        const headers = { 'X-User-Id': currentUser?.id || '' };
        const response = await axios.get(`${API}/expenses/analysis`, {
          headers,
          params: {
            pharmacy_ids: pharmacyIds,
            start_date: start,
            end_date: end
          }
        });
        setAnalysisData(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'analyse:', error);
      }
    };
    loadAnalysis();
  }, [selectedPharmacyIds, dateRange, superAdminPharmacyFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // For super admin, use the pharmacy filter directly
      let pharmacyIds;
      if (currentUser?.role === 'super_admin') {
        // Super admin: use the filter value directly
        if (superAdminPharmacyFilter.length === 0) {
          pharmacyIds = 'all'; // Aucune pharmacie sélectionnée = toutes
        } else {
          pharmacyIds = superAdminPharmacyFilter.join(',');
        }
      } else {
        // Regular admin: use pharmacy context
        pharmacyIds = getPharmacyIdsParam();
      }
      
      // Add date range filtering
      const start = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd'T'00:00:00") : "2000-01-01T00:00:00";
      const end = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd'T'23:59:59") : "2100-12-31T23:59:59";
      
      const headers = {
        'X-User-Id': currentUser?.id || ''
      };
      
      // Déterminer si on doit filtrer par created_by
      // Admin et Super Admin voient tout, les autres voient uniquement leurs données
      const shouldFilterByUser = currentUser?.role !== 'admin' && currentUser?.role !== 'super_admin';
      const createdByFilter = shouldFilterByUser ? currentUser?.id : undefined;
      
      const expensesParams = {
        pharmacy_ids: pharmacyIds,
        start_date: start,
        end_date: end,
        _t: new Date().getTime()
      };
      
      if (createdByFilter) {
        expensesParams.created_by = createdByFilter;
      }
      
      const [expensesRes, categoriesRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/expenses`, { 
          headers: { ...headers, 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }, 
          params: expensesParams 
        }),
        axios.get(`${API}/categories`, { headers, params: { type: 'expense', pharmacy_ids: pharmacyIds } }),
        axios.get(`${API}/suppliers`, { headers: { ...headers, 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }, params: { pharmacy_ids: pharmacyIds, _t: new Date().getTime() } }),
      ]);
      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data);
      setSuppliers(suppliersRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Fonction réutilisable pour rafraîchir l'analyse
  const refreshAnalysis = async () => {
    try {
      let pharmacyIds;
      if (currentUser?.role === 'super_admin') {
        if (superAdminPharmacyFilter.length === 0) {
          pharmacyIds = 'all';
        } else {
          pharmacyIds = superAdminPharmacyFilter.join(',');
        }
      } else {
        pharmacyIds = getPharmacyIdsParam();
      }
      
      const start = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd'T'00:00:00") : "2000-01-01T00:00:00";
      const end = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd'T'23:59:59") : "2100-12-31T23:59:59";
      
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      // Déterminer si on doit filtrer par created_by
      const shouldFilterByUser = currentUser?.role !== 'admin' && currentUser?.role !== 'super_admin';
      const analysisParams = {
        pharmacy_ids: pharmacyIds,
        start_date: start,
        end_date: end
      };
      
      if (shouldFilterByUser) {
        analysisParams.created_by = currentUser?.id;
      }
      
      const response = await axios.get(`${API}/expenses/analysis`, {
        headers,
        params: analysisParams
      });
      setAnalysisData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'analyse:', error);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 10MB)');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez JPG, PNG, WEBP ou PDF.');
      return;
    }

    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload-invoice`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const imageUrl = response.data.url;
      setFormData({ ...formData, invoice_image_url: imageUrl });
      setInvoicePreview(`${BACKEND_URL}${imageUrl}`);
      toast.success('Photo téléchargée avec succès!');
    } catch (error) {
      toast.error("Erreur lors du téléchargement de la photo");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const removeInvoiceImage = () => {
    setFormData({ ...formData, invoice_image_url: '' });
    setInvoicePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category_id) {
      toast.error('Veuillez sélectionner une catégorie');
      return;
    }

    try {
      const headers = {
        'X-User-Id': currentUser?.id || ''
      };
      
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: parseSafeDate(formData.date).toISOString(),
      };

      if (editingExpense) {
        // Mode édition
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload, { headers });
        toast.success('Dépense modifiée avec succès!');
      } else {
        // Mode ajout
        await axios.post(`${API}/expenses`, payload, { headers });
        toast.success('Dépense ajoutée avec succès!');
      }

      setDialogOpen(false);
      resetForm();
      await fetchData();
      await refreshAnalysis(); // Rafraîchir l'analyse
    } catch (error) {
      toast.error(editingExpense ? "Erreur lors de la modification" : "Erreur lors de l'ajout de la dépense");
    }
  };

  const handleWizardSubmit = async (wizardData) => {
    if (!currentUser?.id) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    try {
      const headers = { 'X-User-Id': currentUser.id };

      // ====== Invoice/BL Payment flow ======
      if (wizardData.expenseType === 'invoice' && wizardData.document) {
        const invoicePayload = {
          document_id: wizardData.document.id,
          document_type: wizardData.document.type,
          amount: parseFloat(wizardData.amount),
          payment_method: wizardData.payment_method || 'cash',
          date: wizardData.date ? toSafeISO(wizardData.date) : toSafeISO(new Date()),
        };
        await axios.post(`${API}/expenses/invoice-payment`, invoicePayload, { headers });
        toast.success('Paiement enregistré et dépense créée avec succès !');
        setWizardOpen(false);
        setEditingExpense(null);
        await fetchData();
        await refreshAnalysis();
        return;
      }

      // ====== Free expense flow ======
      // Use _editId and _editCategoryId from wizard data (passed directly, not from closure)
      const isEdit = !!wizardData._editId;
      const editId = wizardData._editId;
      const editCategoryId = wizardData._editCategoryId;

      let categoryId;
      if (isEdit && editCategoryId) {
        categoryId = editCategoryId;
      } else {
        const categoryMapping = {
          'Achat': ['Achats', 'Achat'],
          'Charge': ['Charges', 'Charge'],
          'Salaire': ['Salaires', 'Salaire']
        };
        const possibleNames = categoryMapping[wizardData.category] || [wizardData.category];
        const selectedCategory = categories.find(c => 
          possibleNames.some(name => c.name.toLowerCase().includes(name.toLowerCase()))
        );
        if (!selectedCategory) {
          toast.error(`Impossible de trouver une catégorie correspondante dans la base de données`);
          return;
        }
        categoryId = selectedCategory.id;
      }
      
      const payload = {
        date: toSafeISO(wizardData.date),
        amount: parseFloat(wizardData.amount),
        category_id: categoryId,
        subcategory: wizardData.subcategory || null,
        description: wizardData.description || '',
        payment_method: wizardData.payment_method || 'cash',
        pay_state: wizardData.pay_state || 'paid',
        supplier: wizardData.supplier || '',
        invoice_number: wizardData.invoice_number || '',
      };
      
      if (isEdit) {
        await axios.put(`${API}/expenses/${editId}`, payload, { headers });
        toast.success('Dépense modifiée avec succès!');
      } else {
        await axios.post(`${API}/expenses`, payload, { headers });
        toast.success('Dépense ajoutée avec succès!');
      }
      
      setWizardOpen(false);
      setEditingExpense(null);
      await fetchData();
      await refreshAnalysis();
      
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.status === 403) {
        console.warn("Accès refusé. Droits administrateur requis.");
      } else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        toast.error(`Erreur: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
      } else {
        toast.error("Erreur lors de l'opération");
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense?')) return;

    try {
      const headers = {
        'X-User-Id': currentUser?.id || ''
      };
      await axios.delete(`${API}/expenses/${id}`, { headers });
      toast.success('Dépense supprimée avec succès');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setWizardOpen(true); // Ouvrir le wizard au lieu du vieux formulaire
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({
      date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      amount: '',
      category_id: '',
      parent_category: '',
      description: '',
      payment_method: 'cash',
      supplier: '',
      invoice_number: '',
      invoice_image_url: '',
    });
    setInvoicePreview(null);
  };

  const handleViewImage = (imageUrl) => {
    setViewingImage(`${BACKEND_URL}${imageUrl}`);
    setViewImageDialog(true);
  };


  const formatNumberInput = (value) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', { 
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' DA';
  };

  const paymentMethodLabels = {
    cash: 'Espèces',
    card: 'Carte',
    transfer: 'Virement',
    check: 'Chèque',
    traite: 'Traite',
  };

  // Table column filters (inline in header)
  const expenseFilterConfig = [
    { key: 'pharmacy_name', accessor: (r) => r.pharmacy_name || '—' },
    { key: 'parent_category', accessor: (r) => r.parent_category || r.category_name || '—' },
    { key: 'category_name', accessor: (r) => r.subcategory || r.category_name || '—' },
    { key: 'supplier', accessor: (r) => r.supplier || '—' },
    { key: 'payment_method_label', accessor: (r) => paymentMethodLabels[r.payment_method] || r.payment_method || '—' },
    { key: 'created_by_name', accessor: (r) => r.created_by_fullname || r.created_by_username || '—' },
  ];
  const { filters: colFilters, setFilter: setColFilter, getUniqueValues, filteredData: colFilteredExpenses } = useTableFilters(expenses, expenseFilterConfig);

  const filteredExpenses = colFilteredExpenses.filter((exp) => {
    const matchesSearch = !searchTerm || exp.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exp.category_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exp.supplier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exp.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
      
    let matchesTab = true;
    if (activeTab === 'Achats') matchesTab = exp.parent_category === 'Achat';
    else if (activeTab === 'Charges') matchesTab = exp.parent_category === 'Charge';
    else if (activeTab === 'Salaires') matchesTab = exp.parent_category === 'Salaire';

    return matchesSearch && matchesTab;
  });

  const totalAmount = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  // KPIs calculés à partir des données filtrées (synchronisés avec les filtres de colonnes)
  const filteredKPIs = useMemo(() => {
    let total = 0, charges = 0, achats = 0, salaires = 0, versements = 0;
    filteredExpenses.forEach(exp => {
      const amt = exp.amount || 0;
      total += amt;
      const parent = (exp.parent_category || '').toLowerCase();
      const catName = (exp.category_name || exp.category_id || exp.category || '').toLowerCase();
      const desc = (exp.description || '').toLowerCase();

      if (parent.includes('salaire') || catName.includes('salaire') || catName.includes('paie') || catName.includes('remuneration') || desc.includes('salaire')) {
        salaires += amt;
      } else if (parent.includes('charge') || catName.includes('charge')) {
        charges += amt;
      } else if (parent.includes('achat') || catName.includes('achat')) {
        achats += amt;
      } else if (parent.includes('versement') || catName.includes('versement')) {
        versements += amt;
      } else {
        achats += amt; // default
      }
    });
    return { total, charges, achats, salaires, versements };
  }, [filteredExpenses]);

  return (
    <div className="space-y-4 sm:space-y-6 fade-in bg-gray-50 min-h-screen p-3 sm:p-6" data-testid="expenses-page">
      {/* HEADER */}
      <div className="mb-4 sm:mb-6">
        {/* Titre et Avatar */}
        <div className="flex items-start justify-between mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dépenses</h1>

        </div>
        
        {/* GRAND BLOC BLANC */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Filtre Pharmacie - Nouveau composant */}
            <div className="flex-1 min-w-0">
              {currentUser?.role === 'super_admin' && (
                <ExpensePharmacyFilter
                  selectedPharmacies={superAdminPharmacyFilter}
                  onChange={setSuperAdminPharmacyFilter}
                  currentUser={currentUser}
                />
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Filtre Date */}
              {canFilterPeriod('expenses') && (
                <ExpenseDateRangePicker
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                />
              )}
              {/* Bouton Ajouter une dépense */}
              {canCreate('expenses') && (
                <button
                  onClick={() => {
                    setEditingExpense(null);
                    setWizardOpen(true);
                  }}
                  data-testid="add-expense-top-btn"
                  className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 bg-white text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 shadow-sm"
                >
                  <Plus className="w-5 h-5" />
                  Ajouter une dépense
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Bouton Ajouter */}
        <div className="flex justify-end">
        </div>
      </div>

      {/* ANCIEN FILTRE DÉPLACÉ - Maintenant en bas si besoin */}
      <div className="hidden flex-wrap items-center gap-4 mb-6">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Rechercher une dépense..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="expense-search-input"
              className="pl-12 py-6 border-gray-200 rounded-xl bg-white shadow-sm"
            />
          </div>
        </div>
        <div className="flex gap-3">
          <Select className="w-48">
            <SelectTrigger className="border-gray-200 rounded-xl bg-white shadow-sm py-6">
              <SelectValue placeholder="Toutes les catégories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ModernDateRangePicker 
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
        </div>
      </div>

      {/* 4 CARTES KPI - Design moderne selon screenshot */}
      {canViewTotals('expenses') && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3 sm:gap-6 mb-4 sm:mb-6">
          {/* Carte 1: Total Dépenses */}
          <Card className="border-0 shadow-lg rounded-2xl bg-white hover:shadow-xl transition-shadow">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500 mb-2">Total Dépenses</p>
              <div className="text-xl sm:text-3xl font-bold text-gray-900 mb-1">
                {(analysisData?.total_depenses || 0).toLocaleString('fr-DZ')} DA
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-emerald-600">+15,2%</span>
                <span className="text-xs text-gray-400">vs mois dernier</span>
              </div>
              {/* Mini graphique */}
              <div className="mt-4 h-12">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[
                    { val: 400 }, { val: 300 }, { val: 500 }, { val: 450 }, { val: 600 }, { val: 550 }, { val: 700 }
                  ]}>
                    <Line type="monotone" dataKey="val" stroke="#14b8a6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Carte 2: Charges */}
          <Card className="border-0 shadow-lg rounded-2xl bg-white hover:shadow-xl transition-shadow">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500 mb-2">Charges</p>
              <div className="text-xl sm:text-3xl font-bold text-gray-900 mb-1">
                {filteredKPIs.charges.toLocaleString('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-red-600">-7,1%</span>
                <span className="text-xs text-gray-400">vs mois dernier</span>
              </div>
              {/* Mini graphique */}
              <div className="mt-4 h-12">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[
                    { val: 500 }, { val: 450 }, { val: 400 }, { val: 380 }, { val: 360 }, { val: 350 }, { val: 340 }
                  ]}>
                    <Line type="monotone" dataKey="val" stroke="#f97316" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Carte 3: Achats */}
          <Card className="border-0 shadow-lg rounded-2xl bg-white hover:shadow-xl transition-shadow">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500 mb-2">Achats</p>
              <div className="text-xl sm:text-3xl font-bold text-gray-900 mb-1">
                {filteredKPIs.achats.toLocaleString('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-emerald-600">+28,3%</span>
                <span className="text-xs text-gray-400">vs mois dernier</span>
              </div>
              {/* Mini graphique */}
              <div className="mt-4 h-12">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[
                    { val: 200 }, { val: 300 }, { val: 350 }, { val: 400 }, { val: 500 }, { val: 650 }, { val: 850 }
                  ]}>
                    <Line type="monotone" dataKey="val" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Carte 4: Salaires */}
          <Card className="border-0 shadow-lg rounded-2xl bg-white hover:shadow-xl transition-shadow">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500 mb-2">Salaires</p>
              <div className="text-xl sm:text-3xl font-bold text-gray-900 mb-1">
                {filteredKPIs.salaires.toLocaleString('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-emerald-600">+5,3%</span>
                <span className="text-xs text-gray-400">vs mois dernier</span>
              </div>
              {/* Mini graphique */}
              <div className="mt-4 h-12">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[
                    { val: 180 }, { val: 185 }, { val: 190 }, { val: 188 }, { val: 195 }, { val: 198 }, { val: 200 }
                  ]}>
                    <Line type="monotone" dataKey="val" stroke="#a855f7" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SECTION ANALYTICS - 2 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Colonne 1: Répartition des dépenses (Donut Chart) */}
        <Card className="border-0 shadow-lg rounded-2xl bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900">Répartition des dépenses</CardTitle>
              <div className="flex gap-4 text-sm">
                {['Jour', 'Semaine', 'Mois', 'Année'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setRepartitionPeriod(p)}
                    className={repartitionPeriod === p
                      ? 'text-emerald-600 font-semibold border-b-2 border-emerald-600'
                      : 'text-gray-400 hover:text-gray-700'}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const CHART_COLORS = ['#10b981', '#fbbf24', '#3b82f6', '#a855f7', '#ef4444', '#f97316', '#06b6d4', '#ec4899'];
              
              // Filtrer les dépenses par période sélectionnée
              const now = new Date();
              const filteredByPeriod = filteredExpenses.filter(exp => {
                const d = parseSafeDate(exp.date);
                if (repartitionPeriod === 'Jour') return isSameDay(d, now);
                if (repartitionPeriod === 'Semaine') {
                  // Semaine Samedi→Vendredi
                  const day = now.getDay();
                  const diffToSat = day === 6 ? 0 : day + 1;
                  const weekStart = new Date(now); weekStart.setDate(now.getDate() - diffToSat); weekStart.setHours(0,0,0,0);
                  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23,59,59,999);
                  return d >= weekStart && d <= weekEnd;
                }
                if (repartitionPeriod === 'Mois') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                if (repartitionPeriod === 'Année') return d.getFullYear() === now.getFullYear();
                return true;
              });
              
              // Grouper par parent_category
              const groups = {};
              filteredByPeriod.forEach(exp => {
                const key = exp.parent_category || 'Autres';
                groups[key] = (groups[key] || 0) + exp.amount;
              });
              const total = Object.values(groups).reduce((s, v) => s + v, 0);
              const pieData = Object.entries(groups).map(([name, value], i) => ({
                name,
                value: total > 0 ? Math.round(value / total * 1000) / 10 : 0,
                amount: value,
                color: CHART_COLORS[i % CHART_COLORS.length]
              }));
              const topEntry = pieData.length > 0 ? pieData.reduce((a, b) => a.value > b.value ? a : b) : null;
              
              return (
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
                  <div className="flex-shrink-0 w-[160px] h-[160px] sm:w-[200px] sm:h-[200px]">
                    {pieData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(val) => `${val}%`} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Aucune donnée</div>
                    )}
                    {topEntry && (
                      <div className="relative -mt-28 sm:-mt-32 text-center pointer-events-none">
                        <div className="text-xl sm:text-2xl font-bold text-gray-900">{topEntry.value}%</div>
                        <div className="text-xs sm:text-sm text-gray-500">{total.toLocaleString('fr-DZ')} DA</div>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-3">
                    {pieData.map((entry, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                          <span className="text-sm text-gray-700">{entry.name}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{entry.value}%</span>
                      </div>
                    ))}
                    {pieData.length === 0 && <p className="text-sm text-gray-400">Aucune dépense pour cette période</p>}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Colonne 2: Évolution (Bar Chart) */}
        <Card className="border-0 shadow-lg rounded-2xl bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900">Évolution</CardTitle>
              <div className="flex gap-4 text-sm">
                {['Jour', 'Semaine', 'Mois'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setEvolutionPeriod(p)}
                    className={evolutionPeriod === p
                      ? 'text-emerald-600 font-semibold border-b-2 border-emerald-600'
                      : 'text-gray-400 hover:text-gray-700'}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              // Agréger les dépenses dans la date range sélectionnée
              const barData = [];
              if (expenses.length > 0 && dateRange?.from && dateRange?.to) {
                const start = new Date(dateRange.from);
                const end = new Date(dateRange.to);
                
                if (evolutionPeriod === 'Jour') {
                  // Grouper par jour
                  try {
                    const days = eachDayOfInterval({ start, end });
                    days.forEach(day => {
                      const dayTotal = expenses.filter(e => isSameDay(parseSafeDate(e.date), day)).reduce((s, e) => s + e.amount, 0);
                      if (dayTotal > 0 || days.length <= 31) {
                        barData.push({ date: format(day, 'dd/MM', { locale: fr }), amount: dayTotal });
                      }
                    });
                  } catch {}
                } else if (evolutionPeriod === 'Semaine') {
                  // Grouper par semaine (Samedi→Vendredi)
                  const grouped = {};
                  filteredExpenses.forEach(exp => {
                    const d = parseSafeDate(exp.date);
                    const day = d.getDay();
                    const diffToSat = day === 6 ? 0 : day + 1;
                    const weekStart = new Date(d); weekStart.setDate(d.getDate() - diffToSat); weekStart.setHours(0,0,0,0);
                    const key = format(weekStart, 'dd/MM');
                    grouped[key] = (grouped[key] || 0) + exp.amount;
                  });
                  Object.entries(grouped).sort().forEach(([k, v]) => barData.push({ date: `S. ${k}`, amount: v }));
                } else {
                  // Grouper par mois
                  const grouped = {};
                  filteredExpenses.forEach(exp => {
                    const d = parseSafeDate(exp.date);
                    const key = format(d, 'MMM yyyy', { locale: fr });
                    const sortKey = format(d, 'yyyy-MM');
                    if (!grouped[sortKey]) grouped[sortKey] = { date: key, amount: 0 };
                    grouped[sortKey].amount += exp.amount;
                  });
                  Object.keys(grouped).sort().forEach(k => barData.push(grouped[k]));
                }
              }
              
              return (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        formatter={(value) => `${value.toLocaleString('fr-DZ')} DA`}
                      />
                      <Bar dataKey="amount" fill="#10b981" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  {barData.length === 0 && (
                    <div className="text-center py-4 text-gray-400 text-sm">Aucune donnée pour cette période</div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* ANCIENNE CARTE - CACHÉE */}

      {/* Dialog for Add/Edit Expense */}
      {canCreate('expenses') && (
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
            <DialogHeader>
              <DialogTitle>{editingExpense ? 'Modifier la Dépense' : 'Nouvelle Dépense'}</DialogTitle>
              <DialogDescription>
                {editingExpense ? 'Modifiez les informations de la dépense' : 'Enregistrez une nouvelle dépense dans le système'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date et Heure</Label>
                  <Input
                    id="date"
                    data-testid="expense-date-input"
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="parent_category">Catégorie Principale</Label>
                  <Select 
                    value={formData.parent_category} 
                    onValueChange={(value) => {
                      setFormData({ 
                        ...formData, 
                        parent_category: value,
                        category_id: '' // Reset subcategory
                      });
                    }} 
                    required
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Achat / Charge / Salaire" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Achat">Achat</SelectItem>
                      <SelectItem value="Charge">Charge</SelectItem>
                      <SelectItem value="Salaire">Salaire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="category">Sous-catégorie</Label>
                  <Select 
                    value={formData.category_id} 
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })} 
                    required
                    disabled={!formData.parent_category}
                  >
                    <SelectTrigger className="mt-1" data-testid="expense-category-select">
                      <SelectValue placeholder={formData.parent_category ? "Sélectionnez" : "Choisissez catégorie principale"} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories
                        .filter(cat => cat.parent_category === formData.parent_category)
                        .map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Fournisseur - DÉPLACÉ ICI, juste après Catégorie */}
              <div>
                <Label htmlFor="supplier">Fournisseur</Label>
                <Select 
                  value={formData.supplier} 
                  onValueChange={(value) => setFormData({ ...formData, supplier: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="expense-supplier-select">
                    <SelectValue placeholder="Sélectionnez un fournisseur" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.length === 0 ? (
                      <SelectItem value="none" disabled>Aucun fournisseur disponible</SelectItem>
                    ) : (
                      suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.name}>
                          {supplier.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount">Montant (DA)</Label>
                  <Input
                    id="amount"
                    data-testid="expense-amount-input"
                    type="text"
                    value={formData.amount ? formatNumberInput(formData.amount) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/\s/g, '').replace(',', '.');
                      if (!isNaN(rawValue) || rawValue === '') {
                        setFormData({ ...formData, amount: rawValue });
                      }
                    }}
                    onBlur={(e) => {
                      const num = parseFloat(e.target.value.replace(/\s/g, '').replace(',', '.'));
                      if (!isNaN(num)) {
                        setFormData({ ...formData, amount: num.toString() });
                      }
                    }}
                    required
                    className="mt-1"
                    placeholder="100 000,00"
                  />
                </div>
                <div>
                  <Label htmlFor="payment_method">Mode de paiement</Label>
                  <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                    <SelectTrigger className="mt-1" data-testid="expense-payment-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(paymentMethodLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description (facultatif)</Label>
                <Input
                  id="description"
                  data-testid="expense-description-input"
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1"
                  placeholder="Description de la dépense"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="invoice_number">Numéro de facture (optionnel)</Label>
                  <Input
                    id="invoice_number"
                    data-testid="expense-invoice-input"
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="mt-1"
                    placeholder="Numéro de facture"
                  />
                </div>
              </div>

              {/* Invoice Photo Upload */}
              <div>
                <Label>Photo de la Facture (optionnel)</Label>
                <div className="mt-2 space-y-3">
                  {invoicePreview ? (
                    <div className="relative">
                      <img 
                        src={invoicePreview} 
                        alt="Aperçu facture" 
                        className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={removeInvoiceImage}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {/* Camera Button */}
                      <Button
                        type="button"
                        variant="outline"
                        className="h-24 flex flex-col items-center justify-center space-y-2"
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <Camera className="w-6 h-6" />
                        <span className="text-sm">Prendre une Photo</span>
                      </Button>
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleFileInputChange}
                        data-testid="expense-camera-input"
                      />

                      {/* Upload Button */}
                      <Button
                        type="button"
                        variant="outline"
                        className="h-24 flex flex-col items-center justify-center space-y-2"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <Upload className="w-6 h-6" />
                        <span className="text-sm">Télécharger un Fichier</span>
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={handleFileInputChange}
                        data-testid="expense-file-input"
                      />
                    </div>
                  )}
                  {uploading && (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                      <span className="ml-2 text-sm text-gray-600">Téléchargement en cours...</span>
                    </div>
                  )}
                </div>
              </div>

              <Button 
                type="submit" 
                data-testid="expense-submit-btn" 
                className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700"
                disabled={uploading}
              >
                <TrendingDown className="w-4 h-4 mr-2" />
                {editingExpense ? 'Modifier la Dépense' : 'Ajouter la Dépense'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* TABLEAU */}
      {canViewHistory('expenses') ? (
      <Card className="border-0 shadow-lg rounded-2xl bg-white">
        <CardHeader className="border-b border-gray-100 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Onglets de filtrage - scroll horizontal sur mobile */}
            <div className="flex flex-wrap gap-4 sm:gap-6 text-sm pb-1 -mb-1">
              {['Toutes', 'Achats', 'Charges', 'Salaires'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-2 whitespace-nowrap ${
                    activeTab === tab
                      ? 'text-emerald-600 font-semibold border-b-2 border-emerald-600'
                      : 'text-gray-400 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <TrendingDown className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune dépense enregistrée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-100 bg-gray-50">
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase">Date</TableHead>
                    <FilterableHeader label="Pharmacie" allLabel="TOUS" values={getUniqueValues['pharmacy_name'] || []} selected={colFilters.pharmacy_name} onSelect={(v) => setColFilter('pharmacy_name', v)} className="hidden sm:table-cell" />
                    <FilterableHeader label="Type" allLabel="TOUS" values={getUniqueValues['parent_category'] || []} selected={colFilters.parent_category} onSelect={(v) => setColFilter('parent_category', v)} />
                    <FilterableHeader label="Sous-catégorie" allLabel="TOUTES" values={getUniqueValues['category_name'] || []} selected={colFilters.category_name} onSelect={(v) => setColFilter('category_name', v)} className="hidden md:table-cell" />
                    <FilterableHeader label="Fournisseur" allLabel="TOUTES" values={getUniqueValues['supplier'] || []} selected={colFilters.supplier} onSelect={(v) => setColFilter('supplier', v)} className="hidden lg:table-cell" />
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase hidden xl:table-cell">Description</TableHead>
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase">Montant</TableHead>
                    <FilterableHeader label="Paiement" allLabel="TOUS" values={getUniqueValues['payment_method_label'] || []} selected={colFilters.payment_method_label} onSelect={(v) => setColFilter('payment_method_label', v)} className="hidden lg:table-cell" />
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase hidden lg:table-cell">Reçu</TableHead>
                    <FilterableHeader label="Utilisateur" allLabel="TOUS" values={getUniqueValues['created_by_name'] || []} selected={colFilters.created_by_name} onSelect={(v) => setColFilter('created_by_name', v)} className="hidden md:table-cell" />
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense, index) => (
                    <TableRow key={expense.id} data-testid="expense-row" className={`border-b border-gray-100 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <TableCell className="whitespace-nowrap text-gray-900 font-medium text-sm">
                        {format(parseSafeDate(expense.date), 'dd MMM yyyy', { locale: fr })}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge className={`${getPharmacyColor(expense.pharmacy_name).bg} ${getPharmacyColor(expense.pharmacy_name).text} border-0 font-medium`}>
                          {expense.pharmacy_name}
                        </Badge>
                      </TableCell>
                      {/* Type (parent_category) */}
                      <TableCell>
                        {(() => {
                          const label = expense.parent_category || '-';
                          let bg = 'bg-gray-100'; let txt = 'text-gray-700';
                          if (label.includes('Achat')) { bg = 'bg-blue-100'; txt = 'text-blue-700'; }
                          else if (label.includes('Charge')) { bg = 'bg-red-100'; txt = 'text-red-700'; }
                          else if (label.includes('Salaire')) { bg = 'bg-orange-100'; txt = 'text-orange-700'; }
                          else if (label.includes('Banque') || label.includes('Versements')) { bg = 'bg-teal-100'; txt = 'text-teal-700'; }
                          else if (label.includes('Honoraires')) { bg = 'bg-purple-100'; txt = 'text-purple-700'; }
                          else if (label.includes('Impôts')) { bg = 'bg-pink-100'; txt = 'text-pink-700'; }
                          else if (label.includes('Matériel')) { bg = 'bg-yellow-100'; txt = 'text-yellow-700'; }
                          return <Badge className={`${bg} ${txt} border-0 font-medium truncate max-w-[150px]`}>{label}</Badge>;
                        })()}
                      </TableCell>
                      {/* Sous-catégorie (subcategory ou category_name) */}
                      <TableCell className="text-gray-700 text-sm hidden md:table-cell">
                        {(() => {
                          const subLabel = expense.subcategory || expense.category_name;
                          if (!subLabel) return '-';
                          const cfg = getExpenseVisuals(subLabel);
                          const Icon = cfg.icon;
                          return (
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-full ${cfg.bgColor} flex items-center justify-center flex-shrink-0`}>
                                <Icon className={cfg.color} size={16} strokeWidth={2} />
                              </div>
                              <span className="truncate max-w-[180px]">{subLabel}</span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      {/* Fournisseur (de la base fournisseurs) */}
                      <TableCell className="text-gray-700 font-medium text-sm hidden lg:table-cell">
                        {expense.supplier || '-'}
                      </TableCell>
                      {/* Description / Commentaires */}
                      <TableCell className="text-gray-500 text-sm hidden xl:table-cell max-w-[200px] truncate">
                        {expense.description || '-'}
                      </TableCell>
                      <TableCell className="font-bold text-gray-900 whitespace-nowrap">{expense.amount.toLocaleString('fr-DZ')} DA</TableCell>
                      <TableCell className="text-gray-600 text-sm hidden lg:table-cell">
                        {paymentMethodLabels[expense.payment_method] || 'cash'}
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm hidden lg:table-cell">
                        {expense.invoice_image_url ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewImage(expense.invoice_image_url)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 px-2 text-xs"
                          >
                            📄 Voir
                          </Button>
                        ) : 'Aucun'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                            {(expense.created_by_username || 'U').charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm text-gray-700">{expense.created_by_fullname || expense.created_by_username || 'Utilisateur'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canUpdate('expenses') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(expense)}
                            data-testid="edit-expense-btn"
                            className="text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 h-8 w-8 p-0"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          )}
                          {canDelete('expenses') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(expense.id)}
                            data-testid="delete-expense-btn"
                            className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <p className="text-sm text-yellow-700">Vous n'avez pas la permission de voir l'historique des dépenses</p>
        </div>
      )}

      {/* Image Viewer Dialog */}
      <Dialog open={viewImageDialog} onOpenChange={setViewImageDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Facture</DialogTitle>
            <DialogDescription>
              Cliquez sur l'image pour la télécharger
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center items-center p-4">
            {viewingImage && (
              <a href={viewingImage} download target="_blank" rel="noopener noreferrer">
                <img 
                  src={viewingImage} 
                  alt="Facture" 
                  className="max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub24gZGlzcG9uaWJsZTwvdGV4dD48L3N2Zz4=';
                    toast.error('Impossible de charger l\'image');
                  }}
                />
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Expense Wizard */}
      {wizardOpen && (
        <ExpenseWizard
          isOpen={wizardOpen}
          onClose={() => {
            setWizardOpen(false);
            setEditingExpense(null); // Réinitialiser l'édition quand on ferme
          }}
          onSubmit={handleWizardSubmit}
          categories={categories}
          suppliers={suppliers}
          editMode={!!editingExpense}
          initialData={editingExpense}
        />
      )}
    </div>
  );
}

export default Expenses;
